import type { CalendarPlanDto, PlanGap, PostDto } from '@str/api/types';
import { Overlay } from '@/components/Overlay';
import { CHANNEL_META, SCHEDULE_TIMES, isoDateIn } from '@str/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, useSyncExternalStore, type DragEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { ChannelDots, ErrorText } from '@/components/ui';
import { errorMessage } from '@/lib/api';
import { canDropOn, canMove, dayWithMonth, movedLabel } from '@/lib/calendar-move';
import { createHref, fullDay, gapLabel, inWindow, slotOn } from '@/lib/calendar-plan';
import { DAYS, monthName, toIsoDate } from '@/lib/format';
import { whenParts } from '@/lib/home';
import { postLine, postName } from '@/lib/post-title';
import { useActiveProperty } from '@/lib/property-store';
import { keys, postsApi, usePlan, usePosts } from '@/lib/queries';
import { mediaStyle } from '@/lib/scenes';
import { useModalDialog } from '@/lib/use-modal-dialog';
import {
  fromInputTime,
  hostTimeZone,
  isPreset,
  isSlotPast,
  openSlotOn,
  scheduleZone,
  toInputTime,
  type ScheduleTime,
} from '@/lib/schedule';

type Moved = { post: PostDto; from: { date: string; time: ScheduleTime }; label: string };

/** Below this width seven columns leave a post chip too narrow to read: show an agenda instead. */
const NARROW = '(max-width: 640px)';
function useNarrow() {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window.matchMedia !== 'function') return () => {};
      const mq = window.matchMedia(NARROW);
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => typeof window.matchMedia === 'function' && window.matchMedia(NARROW).matches,
    () => false,
  );
}

/** Where a post opens: the editor until it goes out; its results once it has; imported ones
 * have no page of their own, so the Published list. Same rules as the Posts list. */
function postHref(p: PostDto) {
  if (p.imported) return null;
  return p.status === 'published' ? `/app/posts/${p.id}/results` : `/app/create?post=${p.id}`;
}

export function Calendar() {
  const { active } = useActiveProperty();
  const posts = usePosts(active.id);
  const plan = usePlan(active.id).data;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const narrow = useNarrow();
  const newPostId = (location.state as { newPostId?: string } | null)?.newPostId ?? null;
  const today = new Date();
  // Today on the property's clock: the day a post can still be dropped on.
  const todayIso = isoDateIn(today, active.timezone ?? hostTimeZone());
  // The month today falls in on the property's clock: where the calendar opens and Today returns.
  const monthOf = (iso: string) =>
    new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, 1);
  const [cursor, setCursor] = useState(() => monthOf(todayIso));
  // The coming window the month target counts, on the plan's clock.
  const planToday = plan ? isoDateIn(today, plan.timezone) : null;
  const addable = (iso: string) =>
    !!plan && !!planToday && inWindow(iso, planToday, plan.windowDays);
  const fill = (gap: PlanGap) =>
    void navigate(createHref({ ideaId: plan?.nextIdeaId, date: gap.date, time: gap.time }));
  // The gaps the phone agenda lists: the ones in the month shown, all of them on this month.
  const cursorPrefix = toIsoDate(cursor).slice(0, 7);
  const agendaGaps = (plan?.gaps ?? []).filter(
    (g) => cursorPrefix === todayIso.slice(0, 7) || g.date.startsWith(cursorPrefix),
  );
  const [drag, setDrag] = useState<PostDto | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [moving, setMoving] = useState<PostDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [moved, setMoved] = useState<Moved | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const rows = Math.max(5, Math.ceil((offset + daysInMonth) / 7));
  const cells = Array.from(
    { length: rows * 7 },
    (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i - offset + 1),
  );
  const dated = (posts.data ?? []).filter(
    (p) => p.scheduledDate && p.status !== 'draft' && !p.archivedAt,
  );
  // Every post on a day, earliest first: a busy day shows the first two and says how many more.
  const byDate = new Map<string, PostDto[]>();
  for (const p of [...dated].sort((a, b) =>
    (a.scheduledAt ?? a.scheduledDate!).localeCompare(b.scheduledAt ?? b.scheduledDate!),
  ))
    byDate.set(p.scheduledDate!, [...(byDate.get(p.scheduledDate!) ?? []), p]);
  const [dayOpen, setDayOpen] = useState<string | null>(null);
  /** One post opens itself: the editor until it goes out, its results once it has. */
  const openPost = (p: PostDto) => {
    if (!p.imported && p.status !== 'published') void navigate(`/app?post=${p.id}`);
    else {
      const href = postHref(p);
      if (href) void navigate(href);
      else void navigate('/app/posts', { state: { tab: 'Published' } });
    }
  };

  const say = (m: Moved | null, ms = 6000) => {
    window.clearTimeout(toastTimer.current);
    setMoved(m);
    if (m) toastTimer.current = window.setTimeout(() => setMoved(null), ms);
  };

  /** One call moves the post: same channels, a new day and time. */
  const reschedule = async (
    post: PostDto,
    date: string,
    time: ScheduleTime,
    label: (iso: string, t: ScheduleTime) => string,
  ) => {
    const from = {
      date: post.scheduledDate!,
      time: post.scheduledTime ?? '6:30 PM',
    };
    setBusy(true);
    setError(null);
    try {
      const { post: next } = await postsApi.schedule(post.id, {
        date,
        time,
        channels: post.channels,
        publishNow: false,
        // The day and time stay on the clock the post was scheduled with.
        timezone: scheduleZone(post),
      });
      await qc.invalidateQueries({ queryKey: keys.posts(active.id) });
      void qc.invalidateQueries({ queryKey: keys.plan(active.id) });
      setMoving(null);
      say({ post: next, from, label: label(date, time) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not move the post');
    } finally {
      setBusy(false);
    }
  };
  /** Off the calendar: a draft again, nothing goes out; the Drafts tab shows it landed. */
  const unschedule = async (p: PostDto) => {
    setBusy(true);
    setError(null);
    try {
      await postsApi.unschedule(p.id);
      await qc.invalidateQueries({ queryKey: keys.posts(active.id) });
      void qc.invalidateQueries({ queryKey: keys.plan(active.id) });
      setMoving(null);
      void navigate('/app/posts', { state: { tab: 'Drafts', toast: 'Moved back to drafts' } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not move the post back to drafts');
    } finally {
      setBusy(false);
    }
  };
  const undo = (m: Moved) => {
    say(null);
    void reschedule(
      m.post,
      m.from.date,
      m.from.time,
      (iso, t) => `Moved back to ${dayWithMonth(iso)}, ${t}`,
    );
  };
  const dropCheck = (iso: string) =>
    drag ? canDropOn(iso, drag, todayIso, dated, { zone: scheduleZone(drag) }) : null;
  const onDrop = (e: DragEvent, iso: string) => {
    e.preventDefault();
    const p = drag;
    // A refused day keeps the drag alive; the browser ends it and dragEnd clears it.
    if (!p || !canDropOn(iso, p, todayIso, dated, { zone: scheduleZone(p) }).ok) return;
    setDrag(null);
    setOver(null);
    // Dropped on today after its own time has gone by: the next slot still ahead today.
    const slot = openSlotOn(iso, p.scheduledTime ?? '6:30 PM', scheduleZone(p));
    void reschedule(p, iso, slot.time, movedLabel);
  };
  // Undo puts the post back where it was; not when that time has since gone by.
  const undoGone = moved
    ? isSlotPast(moved.from.date, moved.from.time, scheduleZone(moved.post))
    : false;

  return (
    <div
      className="a-rise cal-page"
      style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <div className="cal-head">
        <div className="cal-head-main">
          <div className="cal-head-left">
            <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-.03em' }}>
              {monthName(cursor)}
              {cursor.getFullYear() !== today.getFullYear() && (
                <span style={{ color: '#999', fontWeight: 600 }}> {cursor.getFullYear()}</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                type="button"
                aria-label="Previous month"
                className="icon-btn hv"
                style={{ width: 30, height: 30, fontSize: 13, color: '#555' }}
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              >
                ‹
              </button>
              <button
                type="button"
                aria-label="Next month"
                className="icon-btn hv"
                style={{ width: 30, height: 30, fontSize: 13, color: '#555' }}
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              >
                ›
              </button>
              <button
                type="button"
                className="btn btn-ghost hv"
                style={{ fontSize: 12, padding: '4px 10px' }}
                onClick={() => setCursor(monthOf(todayIso))}
              >
                Today
              </button>
            </div>
            <span className="cal-hint">
              Drag a scheduled post to another day, or press Enter on it.
            </span>
          </div>
          <PlanLine plan={plan} />
        </div>
        <PlanButton plan={plan} onFill={fill} go={(to) => void navigate(to)} />
      </div>
      {posts.isError && !posts.data ? (
        <div
          role="alert"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <ErrorText>Couldn&apos;t load your posts. {errorMessage(posts.error)}</ErrorText>
          <button type="button" className="btn-pill hv" onClick={() => void posts.refetch()}>
            Try again
          </button>
        </div>
      ) : narrow ? (
        <Agenda
          cursor={cursor}
          posts={dated}
          gaps={agendaGaps}
          onFill={fill}
          todayIso={todayIso}
          newPostId={newPostId}
          onMove={(p) => {
            setError(null);
            setMoving(p);
          }}
        />
      ) : (
        <>
          <div className="cal-days">
            {DAYS.map((d) => (
              <span key={d} style={{ padding: '4px 8px' }}>
                {d}
              </span>
            ))}
          </div>
          <div className="cal-grid">
            {cells.map((d, i) => {
              const iso = toIsoDate(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const list = byDate.get(iso) ?? [];
              const shown = list.slice(0, 2);
              const more = list.length - shown.length;
              const isNew = list.some((p) => p.id === newPostId);
              const future = iso >= todayIso;
              const clickable = list.length > 0 || (inMonth && future);
              const check = over === iso ? dropCheck(iso) : null;
              const open = () => {
                if (list.length > 1) setDayOpen(iso);
                else if (list[0]) openPost(list[0]);
                else if (inMonth && future) void navigate(`/app/create?date=${iso}`);
              };
              const dayChannels = [...new Set(list.flatMap((p) => p.channels))];
              return (
                <div
                  key={iso}
                  data-date={iso}
                  aria-current={iso === todayIso ? 'date' : undefined}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={open}
                  onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && open()}
                  onDragOver={(e) => {
                    if (!drag) return;
                    if (canDropOn(iso, drag, todayIso, dated, { zone: scheduleZone(drag) }).ok)
                      e.preventDefault();
                    if (over !== iso) setOver(iso);
                  }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node) && over === iso)
                      setOver(null);
                  }}
                  onDrop={(e) => onDrop(e, iso)}
                  className={clickable ? 'hv cal-cell' : ''}
                  style={{
                    position: 'relative',
                    borderRadius: 12,
                    background: check?.ok ? '#f4f2ed' : inMonth ? '#fff' : 'transparent',
                    border: isNew
                      ? '1.5px solid #111'
                      : check?.ok
                        ? '1.5px solid #111'
                        : iso === todayIso
                          ? '1.5px solid rgba(0,0,0,.3)'
                          : '1px solid rgba(0,0,0,.07)',
                    padding: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    opacity: inMonth ? 1 : 0.4,
                    minHeight: 0,
                    animation: `rise .45s both${isNew ? ', pulse 1.2s .3s 2' : ''}`,
                    animationDelay: `${(0.02 + i * 0.012).toFixed(3)}s`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#333',
                    }}
                  >
                    <span
                      style={
                        iso === todayIso
                          ? {
                              background: '#111',
                              color: '#fff',
                              borderRadius: 999,
                              minWidth: 20,
                              height: 20,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '0 5px',
                            }
                          : undefined
                      }
                    >
                      {d.getDate()}
                    </span>
                    {list.length > 0 && <ChannelDots channels={dayChannels} />}
                  </div>
                  {check && !check.ok && (
                    <span
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: '#555',
                        background: '#f4f2ed',
                        padding: '2px 7px',
                        borderRadius: 999,
                      }}
                    >
                      {check.why}
                    </span>
                  )}
                  {list.length > 0 ? (
                    <>
                      {shown.map((p) => {
                        const movable = canMove(p);
                        return (
                          <div
                            key={p.id}
                            className={movable ? 'cal-tile' : undefined}
                            role={movable ? 'button' : undefined}
                            tabIndex={movable ? 0 : undefined}
                            aria-label={
                              movable
                                ? `${postName(p)} · ${dayWithMonth(iso)}, ${p.scheduledTime ?? ''} · drag or press Enter to move`
                                : undefined
                            }
                            draggable={movable}
                            onDragStart={(e) => {
                              if (!movable) return;
                              e.dataTransfer?.setData('text/plain', p.id);
                              setDrag(p);
                            }}
                            onDragEnd={() => {
                              setDrag(null);
                              setOver(null);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openPost(p);
                            }}
                            onKeyDown={(e) => {
                              if (movable && e.key === 'Enter') {
                                e.stopPropagation();
                                setError(null);
                                setMoving(p);
                              }
                            }}
                            style={{
                              flex: 1,
                              position: 'relative',
                              borderRadius: 8,
                              ...mediaStyle({ url: p.imageUrl, scene: p.scene }),
                              boxShadow: 'inset 0 -30px 40px rgba(0,0,0,.2)',
                              display: 'flex',
                              alignItems: 'flex-end',
                              padding: 6,
                              minHeight: 0,
                              cursor: movable ? 'grab' : undefined,
                              opacity: drag?.id === p.id ? 0.5 : 1,
                              animation: p.id === newPostId ? 'pop .6s both' : 'none',
                            }}
                          >
                            {/* On a tablet the cell only fits the time: the rest hides (R28). */}
                            <span className="cal-chip-label">
                              {p.status === 'failed' ? 'Try again · ' : ''}
                              {p.scheduledAt && (
                                <span className="cal-chip-time">
                                  {whenParts(p.scheduledAt, p.scheduledTime, p.timezone).time}
                                </span>
                              )}
                              <span className="cal-chip-rest">
                                {p.scheduledAt ? ' · ' : ''}
                                {postLine(p)}
                              </span>
                            </span>
                            {movable && (
                              <button
                                type="button"
                                className="cal-move hv"
                                aria-label={`Move ${postName(p)}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setError(null);
                                  setMoving(p);
                                }}
                              >
                                Move
                              </button>
                            )}
                          </div>
                        );
                      })}
                      {more > 0 && (
                        <button
                          type="button"
                          className="hv"
                          aria-label={`+${more} more on ${dayWithMonth(iso)}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDayOpen(iso);
                          }}
                          style={{
                            alignSelf: 'flex-start',
                            border: 0,
                            background: '#f4f2ed',
                            color: '#333',
                            font: 'inherit',
                            fontSize: 10.5,
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: 999,
                            cursor: 'pointer',
                          }}
                        >
                          +{more} more
                        </button>
                      )}
                    </>
                  ) : addable(iso) ? (
                    <button
                      type="button"
                      className="cal-add"
                      aria-label={`Add a post on ${fullDay(iso)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        void navigate(createHref({ date: iso, time: slotOn(iso, plan!.gaps) }));
                      }}
                    >
                      <span className="cal-add-mark" aria-hidden>
                        +
                      </span>
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      )}

      {dayOpen && (
        <DayPosts
          iso={dayOpen}
          posts={byDate.get(dayOpen) ?? []}
          onClose={() => setDayOpen(null)}
          onMove={(p) => {
            setDayOpen(null);
            setError(null);
            setMoving(p);
          }}
        />
      )}

      {moving && (
        <MoveTo
          post={moving}
          todayIso={todayIso}
          zone={scheduleZone(moving)}
          others={dated}
          busy={busy}
          error={error}
          onClose={() => {
            setMoving(null);
            setError(null);
          }}
          onMove={(date, time) => void reschedule(moving, date, time, movedLabel)}
          onUnschedule={() => void unschedule(moving)}
        />
      )}

      {moved && (
        <div
          className="toast"
          role="status"
          style={{ display: 'flex', alignItems: 'center', gap: 14 }}
        >
          {moved.label}
          <button
            type="button"
            onClick={() => undo(moved)}
            disabled={busy || undoGone}
            title={undoGone ? 'The original time has already passed' : undefined}
            style={{
              border: 0,
              background: 'rgba(255,255,255,.16)',
              color: '#fff',
              font: 'inherit',
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: 999,
              cursor: 'pointer',
            }}
          >
            {undoGone ? 'Original time has passed' : 'Undo'}
          </button>
        </div>
      )}
      {error && !moving && (
        <div className="toast" role="alert" style={{ background: '#b3261e' }}>
          {error}
        </div>
      )}
    </div>
  );
}

/** The month target under the month name: planned of target, a thin bar, the wallet in posts.
 * Its room is kept while the plan loads, so nothing jumps when it lands. */
function PlanLine({ plan }: { plan: CalendarPlanDto | undefined }) {
  if (!plan) return <div className="cal-plan" aria-hidden />;
  const full = plan.planned >= plan.target;
  const pct = Math.min(100, Math.round((plan.planned / Math.max(1, plan.target)) * 100));
  const c = plan.credits;
  return (
    <div className="cal-plan">
      <span className="cal-plan-line">
        {full
          ? 'Your month is planned'
          : `${plan.planned} of ${plan.target} posts planned this month`}
      </span>
      <span
        className="cal-plan-bar"
        role="progressbar"
        aria-label="Posts planned this month"
        aria-valuemin={0}
        aria-valuemax={plan.target}
        aria-valuenow={Math.min(plan.planned, plan.target)}
      >
        <span style={{ width: `${pct}%` }} />
      </span>
      {c && (
        <span className="cal-plan-credits">
          {/* Says what it assumes: a single photo as shot, the cheapest post (photoPosts is the
              balance over that price, worked out by the API from the shared credit costs). */}
          {c.balance} credit{c.balance === 1 ? '' : 's'} · about {c.photoPosts} single-photo post
          {c.photoPosts === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );
}

/** The header's one primary action, named for what it does next: fill the first gap, add
 * another to a full month, or connect a channel first. Create, as before, while the plan loads. */
function PlanButton({
  plan,
  onFill,
  go,
}: {
  plan: CalendarPlanDto | undefined;
  onFill: (gap: PlanGap) => void;
  go: (to: string) => void;
}) {
  const gap = plan?.gaps[0];
  const [label, run] = !plan
    ? ['+ Create', () => go('/app/create')]
    : !plan.channelsConnected
      ? ['Connect a channel to schedule', () => go('/app/channels')]
      : plan.planned >= plan.target
        ? ['Add another', () => go('/app/create')]
        : gap
          ? [`Fill ${gapLabel(gap)}`, () => onFill(gap)]
          : ['+ Create', () => go('/app/create')];
  return (
    <button type="button" className="btn btn-primary hv cal-head-cta" onClick={run}>
      {label}
    </button>
  );
}

/** Move to: the day and the time, the channels as they are. Small, one decision. */
function MoveTo({
  post,
  todayIso,
  zone,
  others,
  busy,
  error,
  onClose,
  onMove,
  onUnschedule,
}: {
  post: PostDto;
  todayIso: string;
  zone: string;
  others: PostDto[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onMove: (date: string, time: ScheduleTime) => void;
  onUnschedule: () => void;
}) {
  const [date, setDate] = useState(post.scheduledDate ?? todayIso);
  const [time, setTime] = useState<ScheduleTime>(post.scheduledTime ?? '6:30 PM');
  const [customOpen, setCustomOpen] = useState(() => !isPreset(time));
  const [why, setWhy] = useState<string | null>(null);
  const slotPast = (iso: string, t: ScheduleTime) => isSlotPast(iso, t, zone);
  const pickDay = (iso: string) => {
    setDate(iso);
    if (iso && slotPast(iso, time)) {
      const open = openSlotOn(iso, time, zone);
      if (open.date === iso) {
        setTime(open.time);
        if (!isPreset(open.time)) setCustomOpen(true);
      }
    }
  };
  const submit = () => {
    const check = canDropOn(date, post, todayIso, others, { zone });
    if (!check.ok && !(check.why === 'Same day' && time !== post.scheduledTime)) {
      setWhy(check.why === 'Same day' ? 'It is already on that day at that time' : check.why);
      return;
    }
    if (slotPast(date, time)) {
      setWhy('That time has already passed. Pick a later one.');
      return;
    }
    setWhy(null);
    onMove(date, time);
  };
  return (
    <Overlay onClose={onClose}>
      <div
        role="dialog"
        aria-label="Move to"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        style={{
          width: 360,
          maxWidth: 'calc(100vw - 32px)',
          background: '#fff',
          borderRadius: 16,
          border: '1.5px solid rgba(0,0,0,.1)',
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          boxShadow: '0 24px 60px -24px rgba(0,0,0,.4)',
        }}
      >
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.02em' }}>Move to</div>
          <div style={{ fontSize: 12.5, color: '#777', marginTop: 2 }}>{postName(post)}</div>
        </div>
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            fontSize: 11.5,
            fontWeight: 700,
            color: '#888',
            textTransform: 'uppercase',
            letterSpacing: '.07em',
          }}
        >
          Day
          <input
            type="date"
            value={date}
            min={todayIso}
            onChange={(e) => pickDay(e.target.value)}
            style={{
              font: 'inherit',
              fontSize: 15,
              padding: '10px 12px',
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,.12)',
              textTransform: 'none',
              letterSpacing: 0,
            }}
          />
        </label>
        <div
          role="radiogroup"
          aria-label="Time"
          className="seg round"
          style={{ alignSelf: 'flex-start' }}
        >
          {SCHEDULE_TIMES.map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={!customOpen && time === t}
              disabled={slotPast(date, t)}
              title={slotPast(date, t) ? 'Already passed today' : undefined}
              className={`seg-item hv${!customOpen && time === t ? ' on' : ''}`}
              onClick={() => {
                setCustomOpen(false);
                setTime(t);
              }}
            >
              {t}
            </button>
          ))}
          <button
            type="button"
            role="radio"
            aria-checked={customOpen}
            className={`seg-item hv${customOpen ? ' on' : ''}`}
            onClick={() => setCustomOpen(true)}
          >
            Pick a time
          </button>
        </div>
        {customOpen && (
          <input
            type="time"
            step={60}
            aria-label="Custom time"
            value={toInputTime(time)}
            onChange={(e) => {
              const t = fromInputTime(e.target.value);
              if (t) setTime(t);
            }}
            style={{
              font: 'inherit',
              fontSize: 15,
              padding: '10px 12px',
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,.12)',
              alignSelf: 'flex-start',
            }}
          />
        )}
        <div
          style={{ fontSize: 12.5, color: '#555', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <ChannelDots channels={post.channels} />
          {post.channels.map((c) => CHANNEL_META[c].label).join(', ')} · same as now
        </div>
        {(why || error) && <ErrorText>{why ?? error}</ErrorText>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-outline hv"
            style={{ marginRight: 'auto' }}
            onClick={onUnschedule}
            disabled={busy}
            title="Off the calendar and back to drafts; nothing goes out"
          >
            Move back to drafts
          </button>
          <button type="button" className="btn btn-outline hv" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary hv" onClick={submit} disabled={busy}>
            {busy ? 'Moving…' : 'Move'}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

/** The phone month: one row per day that has posts, each post on its own line, tappable. */
function Agenda({
  cursor,
  posts,
  gaps,
  onFill,
  todayIso,
  newPostId,
  onMove,
}: {
  cursor: Date;
  posts: PostDto[];
  /** The plan's open slots to list after the posts, soonest first. */
  gaps: PlanGap[];
  onFill: (gap: PlanGap) => void;
  todayIso: string;
  newPostId: string | null;
  onMove: (p: PostDto) => void;
}) {
  const prefix = toIsoDate(cursor).slice(0, 7);
  const inMonth = posts
    .filter((p) => p.scheduledDate!.startsWith(prefix))
    .sort((a, b) =>
      (a.scheduledAt ?? a.scheduledDate!).localeCompare(b.scheduledAt ?? b.scheduledDate!),
    );
  const days = new Map<string, PostDto[]>();
  for (const p of inMonth) days.set(p.scheduledDate!, [...(days.get(p.scheduledDate!) ?? []), p]);
  if (days.size === 0 && gaps.length === 0)
    return <p className="cal-agenda-empty">Nothing on the calendar in {monthName(cursor)}.</p>;
  const open = gaps.length > 0 && (
    <ul className={`cal-gaps${days.size === 0 ? ' is-alone' : ''}`} aria-label="Open slots">
      {gaps.map((g) => (
        <li key={`${g.date} ${g.time}`} className="cal-gap">
          <span className="cal-gap-when">Open · {gapLabel(g)}</span>
          <button
            type="button"
            className="btn btn-outline hv cal-gap-fill"
            aria-label={`Fill it, ${gapLabel(g)}`}
            onClick={() => onFill(g)}
          >
            Fill it
          </button>
        </li>
      ))}
    </ul>
  );
  if (days.size === 0) return open;
  return (
    <>
      <ol className="cal-agenda" aria-label={`Posts in ${monthName(cursor)}`}>
        {[...days].map(([iso, list]) => {
          const d = new Date(
            Number(iso.slice(0, 4)),
            Number(iso.slice(5, 7)) - 1,
            Number(iso.slice(8)),
          );
          return (
            <li
              key={iso}
              data-date={iso}
              className="cal-agenda-day"
              aria-current={iso === todayIso ? 'date' : undefined}
            >
              <div className="cal-agenda-date">
                <span>{DAYS[(d.getDay() + 6) % 7]}</span>
                <strong>{d.getDate()}</strong>
              </div>
              <ul className="cal-agenda-posts">
                {list.map((p) => (
                  <AgendaPost key={p.id} post={p} isNew={p.id === newPostId} onMove={onMove} />
                ))}
              </ul>
            </li>
          );
        })}
      </ol>
      {open}
    </>
  );
}

/** One post on a day's list: its time, kind, title and picture, opening the post; Move if it can. */
function AgendaPost({
  post: p,
  isNew,
  onMove,
}: {
  post: PostDto;
  isNew: boolean;
  onMove: (p: PostDto) => void;
}) {
  const href = postHref(p);
  return (
    <li className={isNew ? 'cal-agenda-post is-new' : 'cal-agenda-post'}>
      <Link
        to={href ?? '/app/posts'}
        state={href ? undefined : { tab: 'Published' }}
        className="cal-agenda-link hv"
      >
        <span
          className="cal-agenda-thumb"
          aria-hidden
          style={mediaStyle({ url: p.imageUrl, scene: p.scene })}
        />
        <span className="cal-agenda-text">
          <span className="cal-agenda-time">
            {p.scheduledAt
              ? whenParts(p.scheduledAt, p.scheduledTime, p.timezone).time
              : p.scheduledTime}
            {p.status === 'failed'
              ? ' · Try again'
              : p.status === 'published'
                ? ' · Published'
                : ''}
          </span>
          <span className="cal-agenda-title">{postLine(p)}</span>
        </span>
        <ChannelDots channels={p.channels} />
      </Link>
      {canMove(p) && (
        <button
          type="button"
          className="btn btn-outline hv cal-agenda-move"
          aria-label={`Move ${postName(p)}`}
          onClick={() => onMove(p)}
        >
          Move
        </button>
      )}
    </li>
  );
}

/** A busy day's posts, all of them: what "+N more" opens on the month grid. */
function DayPosts({
  iso,
  posts,
  onClose,
  onMove,
}: {
  iso: string;
  posts: PostDto[];
  onClose: () => void;
  onMove: (p: PostDto) => void;
}) {
  const [dialogRef, onDialogKeyDown] = useModalDialog(onClose);
  const label = dayWithMonth(iso);
  return (
    <Overlay onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        ref={dialogRef}
        onKeyDown={onDialogKeyDown}
        onClick={(e) => e.stopPropagation()}
        style={{
          outline: 'none',
          width: 420,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100% - 48px)',
          overflowY: 'auto',
          background: '#fff',
          borderRadius: 16,
          border: '1.5px solid rgba(0,0,0,.1)',
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          boxShadow: '0 24px 60px -24px rgba(0,0,0,.4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.02em' }}>
            {label} · {posts.length} posts
          </div>
          <button type="button" className="btn btn-outline hv" onClick={onClose}>
            Close
          </button>
        </div>
        <ul className="cal-agenda-posts">
          {posts.map((p) => (
            <AgendaPost key={p.id} post={p} isNew={false} onMove={onMove} />
          ))}
        </ul>
      </div>
    </Overlay>
  );
}
